import { useState } from 'react';
import { useEffect } from 'react';
import {
  Box,
  IconButton,
  Card,
  Grid,
  Typography,
  Tab,
  Tabs,
  CircularProgress,
  Menu,
  MenuItem,
  Badge
} from '@mui/material';

import UserSessions from './UserSessions';
import { ROLES, useAuthState } from 'src/contexts/authContext';
import OrdersListDataGrid from '../orders/OrderListDataGrid';
import UserExternalProfiles from './UserExternalProfiles';
import { TabPanel } from '../../shared/TabPanel';
import UserUnlocks from './UserUnlocks';
import UserCooldowns from './UserCooldowns';
import { useLocation } from 'react-router-dom';
import { GridMoreVertIcon } from '@mui/x-data-grid';
import UserOrderGuestMerge from 'src/components/dashboard/orders/UserOrderGuestMerge';
import { useMutation } from '@apollo/client';
import { UNLOCK_ACCOUNT, RESET_GUEST_SESSIONS } from 'src/graphql/queries';
import UserMetadata from './UserMetadata';
import { Order } from 'src/models/order';
import { Headset, User } from 'src/models/user';
import UserMembershipsCard from './memberships/UserMembershipsCard';
import Check from 'src/icons/Check';
import Ban from 'src/icons/Ban';
import UserIcon from 'src/icons/User';
import CreditCard from 'src/icons/CreditCard';
import Lock from 'src/icons/Lock';
import Clock from 'src/icons/Clock';
import Cog from 'src/icons/Cog';
import UserDetailsProfile from './UserDetailsProfile';
import DeviceTablet from 'src/icons/DeviceTablet';
import CheckCircle from 'src/icons/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Headphones from '@mui/icons-material/Headphones';
import UserHeadsetUsage from './UserHeadsetUsage';

const UserDetails = ({
  executeGetUser,
  userLoading,
  userData,
  userError,
  usersLoading,
  usersData,
  handleClickInvoiceRefund,
  refundInvoiceLoading
}) => {
  const location = useLocation();

  const authState = useAuthState();

  const [numRecentGuestSessions, setNumRecentGuestSessions] =
    useState<number>(0);
  const [showEmailEdit, setShowEmailEdit] = useState<boolean>(false);

  const [user, setUser] = useState<User | undefined>(undefined);
  const [skuMap, setSkuMap] = useState<Map<string, string>>(
    new Map<string, string>()
  );

  const [
    executeResetGuestSessions,
    {
      loading: resetGuestSessionsLoading,
      data: resetGuestSessionsData,
      error: resetGuestSessionsError
    }
  ] = useMutation(RESET_GUEST_SESSIONS);

  const onClickResetGuestSessions = async () => {
    const isReset = await executeResetGuestSessions({
      variables: {
        email: user.email
      }
    });

    if (isReset) {
      setNumRecentGuestSessions(0);
    }
  };

  const mergeOrderData = (orders: Order[]) => {
    let res: Order[] = [];
    for (let i = 0; i < user.orders.length; i++) {
      const order = user.orders[i];
      const updated = orders.find(
        (o) => o.confirmationCode === order.confirmationCode
      );
      if (updated) {
        res.push(updated);
      } else {
        res.push(order);
      }
    }
    user.orders = res;
    setUser(user);
  };

  const [tabValue, setTabValue] = useState<number>(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showMergeGuestOrder, setMergeGuestOrder] = useState<boolean>(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const pathEmail = location.pathname.replace('/dashboard/users/', '');

    if (pathEmail && pathEmail !== 'details') {
      executeGetUser({
        variables: {
          email: pathEmail,
          fetchPolicy: 'no-cache'
        }
      });
    }
  }, []);

  useEffect(() => {
    if (userData?.User) {
      setUser(new User(userData.User));
      setSkuMap(Order.skuMap);

      setShowEmailEdit(false);
      const currentMonth = new Date().getUTCMonth();
      const numGuestSessions =
        userData?.User?.recentGuestSessions.filter(
          (session) =>
            new Date(session.start.formatted).getUTCMonth() === currentMonth &&
            session.state === 'COMPLETE'
        ).length ?? 0;
      setNumRecentGuestSessions(numGuestSessions);
    }
  }, [userData]);

  const noUsersFoundByName = userData?.Users?.length === 0;

  return (
    <>
      {userLoading || usersLoading ? (
        <CircularProgress />
      ) : (
        user &&
        !noUsersFoundByName && (
          <>
            <Grid container mb={3}>
              <Grid item xs={8}>
                <Typography fontSize="" color="textPrimary" variant="h5">
                  User Details: {user.firstName} {user.lastName}
                </Typography>
              </Grid>
              {user?.id && (
                <Grid item xs={3}>
                  <UserExternalProfiles userEmail={user.email} />
                </Grid>
              )}
              <Grid item xs={1}>
                <IconButton
                  aria-label="more"
                  id="long-button"
                  aria-controls={open ? 'long-menu' : undefined}
                  aria-expanded={open ? 'true' : undefined}
                  aria-haspopup="true"
                  onClick={handleClick}
                  sx={{
                    float: 'right'
                  }}
                >
                  <GridMoreVertIcon />
                </IconButton>
                <Menu
                  id="long-menu"
                  MenuListProps={{
                    'aria-labelledby': 'long-button'
                  }}
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  PaperProps={{
                    style: {
                      maxHeight: 48 * 4.5,
                      width: '30ch'
                    }
                  }}
                >
                  {authState.minAuthLevelMet(ROLES.CSA_ADMIN) && (
                    <MenuItem
                      key="merge-guest-order"
                      onClick={() => {
                        handleClose();
                        setMergeGuestOrder(true);
                      }}
                    >
                      Merge Guest Order
                    </MenuItem>
                  )}

                  <MenuItem
                    key="reset-guest-sessions"
                    onClick={() => {
                      onClickResetGuestSessions();
                      handleClose();
                    }}
                    disabled={resetGuestSessionsLoading}
                  >
                    Reset Guest Sessions ({numRecentGuestSessions})
                  </MenuItem>
                </Menu>
              </Grid>
            </Grid>

            {!userLoading && user && (
              <Card sx={{ mt: 3 }}>
                <Box
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: '#abbbc3',
                    button: {
                      fontSize: '0.9rem',
                      color: '#ffffff',
                      '&.Mui-selected': {
                        color: '#0b5466'
                      }
                    },
                    '&.MuiTabs-indicator': {
                      backgroundColor: '#0b5466'
                    }
                  }}
                >
                  <Tabs value={tabValue} onChange={handleTabChange}>
                    <Tab label="Profile" icon={<UserIcon />} />
                    <Tab
                      label="Orders"
                      icon={
                        <Badge badgeContent={user.orders.length}>
                          <CreditCard />
                        </Badge>
                      }
                    />
                    <Tab
                      label={`${user.membership?.shortName ?? 'Personal'} Membership`}
                      icon={
                        user.membership?.isActiveOrTrial() ? (
                          <CheckCircle />
                        ) : user.hasExpiredPersonalMembership() ? (
                          <ErrorOutlineIcon />
                        ) : (
                          <Ban />
                        )
                      }
                    />
                    <Tab
                      label="Professional Membership"
                      icon={
                        user.proMembership?.isActiveOrTrial() ||
                        user.hasActiveProfessionalAccess() ? (
                          <CheckCircle />
                        ) : user.hasExpiredProMembership() ? (
                          <ErrorOutlineIcon />
                        ) : (
                          <Ban />
                        )
                      }
                    />
                    <Tab label="Cooldowns" icon={<Clock />} />
                    <Tab label="Unlocks" icon={<Lock />} />
                    <Tab label="Headsets" icon={<Headphones />} />
                    {(authState.isRole(ROLES.DEV_ADMIN) ||
                      authState.isRole(ROLES.ADMIN)) && (
                      <Tab label="Metadata" icon={<Cog />} />
                    )}
                    {(authState.isRole(ROLES.DEV_ADMIN) ||
                      authState.isRole(ROLES.ADMIN)) && (
                      <Tab label="Sessions" icon={<DeviceTablet />} />
                    )}
                  </Tabs>
                </Box>
                <TabPanel value={tabValue} index={0}>
                  <UserDetailsProfile
                    user={user}
                    executeGetUser={executeGetUser}
                  />
                </TabPanel>
                <TabPanel value={tabValue} index={1}>
                  <OrdersListDataGrid
                    data={{ orders: user.orders, skuMap: skuMap }}
                    loading={userLoading}
                    handleOnPageChange={(p) => {}}
                    mergeOrderData={mergeOrderData}
                    rowCount={user.orders.length}
                    pageSize={user.orders.length}
                  />
                </TabPanel>
                <TabPanel value={tabValue} index={2}>
                  <UserMembershipsCard
                    user={user}
                    loading={userLoading}
                    handleClickInvoiceRefund={handleClickInvoiceRefund}
                    refundInvoiceLoading={refundInvoiceLoading}
                    userLoading={userLoading}
                    refreshUser={(email: string) => {
                      executeGetUser({
                        variables: {
                          email: email,
                          fetchPolicy: 'no-cache'
                        }
                      });
                    }}
                  />
                </TabPanel>
                <TabPanel value={tabValue} index={3}>
                  <UserMembershipsCard
                    user={user}
                    pro={true}
                    loading={userLoading}
                    handleClickInvoiceRefund={handleClickInvoiceRefund}
                    refundInvoiceLoading={refundInvoiceLoading}
                    userLoading={userLoading}
                    refreshUser={(email: string) => {
                      executeGetUser({
                        variables: {
                          email: email,
                          fetchPolicy: 'no-cache'
                        }
                      });
                    }}
                  />
                </TabPanel>
                <TabPanel value={tabValue} index={4}>
                  <UserCooldowns loading={userLoading} user={user} />
                </TabPanel>
                <TabPanel value={tabValue} index={5}>
                  <UserUnlocks loading={userLoading} user={user} />
                </TabPanel>
                <TabPanel value={tabValue} index={6}>
                  <UserHeadsetUsage headsets={user.usedHeadsets ?? []} />
                </TabPanel>
                {(authState.isRole(ROLES.DEV_ADMIN) ||
                  authState.isRole(ROLES.ADMIN)) && (
                  <TabPanel value={tabValue} index={7}>
                    <UserMetadata
                      metadata={user.metadata}
                      userId={user.id}
                      userEmail={user.email}
                      executeGetUser={executeGetUser}
                    />
                  </TabPanel>
                )}
                {(authState.isRole(ROLES.DEV_ADMIN) ||
                  authState.isRole(ROLES.ADMIN)) && (
                  <TabPanel value={tabValue} index={8}>
                    <UserSessions email={user.email} authState={authState} />
                  </TabPanel>
                )}
              </Card>
            )}
          </>
        )
      )}
      {showMergeGuestOrder && user && (
        <UserOrderGuestMerge
          user={user}
          onClose={() => {
            setMergeGuestOrder(false);
          }}
        />
      )}
    </>
  );
};

export default UserDetails;
